import { toast } from "@/components"
import { graphql } from "@/gql"
import {
  AddInstagramAccountDocument,
  GenerateInstagramVerificationCode,
  GenerateInstagramVerificationCodeDocument,
  GetSocialAccountsDocument,
} from "@/gql/graphql"
import { DocumentNode, useApolloClient, useMutation } from "@apollo/client"
import { useEffect, useState } from "react"
import { CopyCodeStep } from "./CopyCodeStep"
import { UsernameStep } from "./UsernameStep"

interface Props {
  instagramFlowSocialId: string | undefined
  closeFlow: () => void
  onVerifyCallback: (value: string) => void
  refetchQueries: DocumentNode[]
}

graphql(`
  mutation AddInstagramAccount($input: AddInstagramAccountInput!) {
    addInstagramAccount(input: $input) {
      errors {
        field
        messages
      }
      socialAccount {
        id
        username
        verified
      }
    }
  }

  mutation GenerateInstagramVerificationCode($socialAccountId: ID!) {
    generateInstagramVerificationCode(socialAccountId: $socialAccountId) {
      code
      recipientUsername
      username
      recipientInstagramProfileLink
    }
  }
`)

export const InstagramFlow: React.FC<Props> = ({
  refetchQueries,
  instagramFlowSocialId,
  closeFlow,
  onVerifyCallback,
}) => {
  const client = useApolloClient()
  const [step, setStep] = useState(1)
  useEffect(() => {
    if (step === 0) {
      closeFlow()
      return
    }
    if (instagramFlowSocialId) {
      setStep(2)
      generateInstagramVerificationCode({
        variables: { socialAccountId: instagramFlowSocialId },
      })
        .then(res => {
          setInstagramCodeData(res.data?.generateInstagramVerificationCode)
        })
        .catch(err => {
          if (err.message === "This account is already verified.") {
            toast.success("This account is already verified.")
            client.query({ query: GetSocialAccountsDocument }).then(() => {
              closeFlow()
            })
          }
        })
    }
  }, [step])

  const [instagramCodeData, setInstagramCodeData] = useState<GenerateInstagramVerificationCode | null | undefined>(null)
  const [socialAccountIdFromMutation, setSocialAccountIdFromMutation] = useState<string | undefined>(undefined)

  const [addInstagramAccount] = useMutation(AddInstagramAccountDocument, {
    refetchQueries: refetchQueries,
  })
  const [generateInstagramVerificationCode] = useMutation(GenerateInstagramVerificationCodeDocument)

  const handleAddInstagramAccount = async (username: string) => {
    const { data: addAccountData } = await addInstagramAccount({
      variables: { input: { username } },
      refetchQueries: refetchQueries,
      onCompleted: data => {
        if (data.addInstagramAccount?.errors?.[0]?.messages?.[0]) {
          toast.error(data.addInstagramAccount?.errors?.[0]?.messages?.[0])
        }
      },
    })
    if (addAccountData?.addInstagramAccount?.socialAccount?.id) {
      setSocialAccountIdFromMutation(addAccountData?.addInstagramAccount?.socialAccount?.id)
      const { data: verificationData } = await generateInstagramVerificationCode({
        variables: { socialAccountId: addAccountData?.addInstagramAccount?.socialAccount?.id },
      })
      if (verificationData?.generateInstagramVerificationCode) {
        setInstagramCodeData(verificationData?.generateInstagramVerificationCode)
        setStep(2)
      }
    }
  }

  return (
    <>
      {step === 1 && <UsernameStep setStep={setStep} handleAddInstagramAccount={handleAddInstagramAccount} />}
      {step === 2 && instagramCodeData && (
        <CopyCodeStep
          instagramCodeData={instagramCodeData}
          setStep={setStep}
          socialAccountId={socialAccountIdFromMutation || instagramFlowSocialId}
          onVerifyCallback={onVerifyCallback}
          refetchQueries={refetchQueries}
        />
      )}
    </>
  )
}
